import { supabase } from './supabase-config';
import invariant from 'tiny-invariant';

import { breadcrumb, log } from '@utils/logging';
import { getSignedInUser } from './supabase-utils';
import { toast } from 'react-toastify';
import {
    AthleteTeamMember,
    DbAthlete,
    DbUser,
    EVENT_ROLE,
    EventMembership,
    InviteUserToEventBody,
    InviteUserToTeamBody,
    RelatedAthlete,
    STATUS,
    TEAM_ROLE,
    TeamAthlete,
    User,
    UserAthlete,
    UserTeam,
} from '@type/db-types';
import {
    RealtimePostgresInsertPayload,
    RealtimePostgresUpdatePayload,
} from '@supabase/supabase-js';
import { AddAthleteFormValues } from '@components/AddWrestler/AddWrestlerUtils';
import { hidePlaceholderEmail } from '@utils/shared-utils';
import { inviteUserToEvent, inviteUserToTeam, updateUserEmail } from '@modules/api';

// const USER_ATHLETE = 'user_athlete';
const USER_TEAM = 'user_team';
const TEAM_MEMBERSHIP = 'TeamAthletes'; // View, NOT table
const USERS = 'users';
const ATHLETES = 'athletes';

export const getUser = async (userId: string): Promise<User> => {
    const user = await getSignedInUser();
    invariant(user, 'User must be signed in to get users');

    const { data, error } = await supabase
        .from(USERS)
        .select(
            `
            *,
            ...RegisteredUsers(email)
        `,
        )
        .eq('id', userId)
        .single();

    if (error) throw Error(error.message);

    breadcrumb('getUserById', { userId, data });

    return data as User;
};

// get user by email
export const getUserByEmail = async (email: string): Promise<User> => {
    const { data, error } = await supabase
        .from('RegisteredUsers')
        .select(`id`)
        .eq('email', email)
        .single();

    breadcrumb('getUserByEmail', { email, data });

    if (error || !data.id) throw Error(error?.message);

    return getUser(data.id);
};

export const getUserIdFromEmail = async (
    email: string,
    parentEntityId: string,
    parentEntityTable: 'event' | 'team',
): Promise<string> => {
    const existingUser = await getUserByEmail(email);

    if (existingUser) return existingUser.id;

    let newUser: null | EventMembership | TeamAthlete = null;

    if (parentEntityTable === 'event') {
        const invite: InviteUserToEventBody = {
            email,
            firstName: '',
            lastName: '',
            role: EVENT_ROLE.OWNER,
            eventId: parentEntityId,
            membershipStatus: STATUS.INVITED,
        };

        newUser = await inviteUserToEvent(invite);
    } else if (parentEntityTable === 'team') {
        const invite: InviteUserToTeamBody = {
            email,
            firstName: '',
            lastName: '',
            role: TEAM_ROLE.OWNER,
            teamId: parentEntityId,
            teamStatus: STATUS.INVITED,
        };

        newUser = await inviteUserToTeam(invite);
    }
    if (newUser) {
        console.log('new user created', newUser);
        return newUser?.userId;
    }
    throw new Error('Error creating user');
};

export function listenToUserChanges(
    userId: string,
    onMessage: (message: User) => void,
): () => void {
    const onCallback = async (
        payload: RealtimePostgresInsertPayload<User> | RealtimePostgresUpdatePayload<User>,
    ) => {
        console.log(`User listener updated: `, payload);
        onMessage(payload.new);
    };

    // Listen to inserts on the user table for the given userId
    const insertQuery = supabase
        .channel('listenToUserInsert')
        .on<User>(
            'postgres_changes',
            {
                event: 'INSERT',
                schema: 'public',
                table: USERS,
                filter: `id=eq.${userId}`,
            },
            onCallback,
        )
        .subscribe();

    // Listen to updates on the user table for the given userId
    const updateQuery = supabase
        .channel('listenToUserUpdate')
        .on<User>(
            'postgres_changes',
            {
                event: 'UPDATE',
                schema: 'public',
                table: USERS,
                filter: `id=eq.${userId}`,
            },
            onCallback,
        )
        .subscribe();

    return () => {
        insertQuery.unsubscribe();
        updateQuery.unsubscribe();
    };
}

export async function updateUserName(
    user: Pick<DbUser, 'id' | 'firstName' | 'lastName'>,
    description: string,
): Promise<DbUser | null> {
    breadcrumb('updateUserName', { user, description });

    const { firstName, lastName, id } = user;

    const { data, error } = await supabase
        .from(USERS)
        .upsert({ firstName, lastName, id })
        .eq('id', user.id)
        .select('*')
        .single();

    if (error) {
        log(`[updateUserName]: ${error.message}`, { error, user, description });
        throw new Error(error.message);
    } else {
        console.log(`user name (${description}) updated `, data);
        return data as DbUser;
    }
}

export async function insertUser(
    user: Omit<DbUser, 'id' | 'email' | 'imageUrl' | 'phone' | 'createdAt' | 'updatedAt'>,
    description: string,
): Promise<DbUser | null> {
    breadcrumb('updateUser', { user, description });

    const { data, error } = await supabase.from(USERS).upsert(user).select('*').single();

    if (error) {
        log(`[updateUser]: ${error.message}`, { error, user, description });
        throw new Error(error.message);
    } else {
        console.log(`user (${description}) updated `, data);
        return data as DbUser;
    }
}

export async function updateUser(
    user: Partial<DbUser>,
    description: string,
): Promise<DbUser | null> {
    breadcrumb('updateUser', { user, description });

    invariant(user?.id, 'user id required to update');

    const { data, error } = await supabase
        .from(USERS)
        .update({ ...user })
        .eq('id', user.id)
        .select('*')
        .single();

    if (error) {
        log(`[updateUser]: ${error.message}`, { error, user, description });
        throw new Error(error.message);
    } else {
        console.log(`user (${description}) updated `, data);
        return data as DbUser;
    }
}

async function updateUserMembership(
    membership: Pick<TeamAthlete, 'membershipId' | 'role' | 'teamStatus'>,
) {
    const { membershipId, role, teamStatus } = membership;
    invariant(membershipId && role && teamStatus, '[updateUserMembership]: missing required keys');
    const { data, error } = await supabase
        .from(USER_TEAM)
        .update({ role, status: teamStatus })
        .eq('id', membershipId)
        .select('*')
        .single();

    if (error) {
        log(`[updateUserMembership]: ${error.message}`, { error, membership });
        throw new Error(error.message);
    } else {
        console.log('user membership updated ', data);
        return data;
    }
}

export async function createUserTeamMembership(
    membership: Pick<UserTeam, 'paymentId' | 'role' | 'status' | 'teamId' | 'userId'>,
) {
    const { role, status, teamId, userId } = membership;
    invariant(
        role && status && teamId && userId,
        '[createUserTeamMembership]: missing required keys',
    );
    const { data, error } = await supabase.from(USER_TEAM).insert(membership).select('*').single();

    if (error) {
        log(`[updateUserMembership]: ${error.message}`, { error, membership });
        throw new Error(error.message);
    } else {
        console.log('user membership updated ', data);
        return data;
    }
}

export async function updateUserAndMembership(
    user: Partial<TeamAthlete> | Pick<User, 'id' | 'firstName' | 'lastName'>,
    description = 'updateUser',
): Promise<User | null> {
    breadcrumb('updateUser', { user, description });

    const { firstName, lastName, id } = user;

    if (!id) {
        throw new Error('User id required to update');
    }

    const { data, error } = await supabase
        .from(USERS)
        .update({ firstName, lastName, id })
        .eq('id', id);

    if ('teamId' in user) {
        updateUserMembership(user as TeamAthlete);
    }

    if (error) {
        log(`[updateUser]: ${error.message}`, { error, user, description });
        throw new Error(error.message);
    } else {
        console.log(`user (${description}) updated `, data);
        return data;
    }
}
interface UpsertAthlete extends Partial<Omit<DbAthlete, 'id' | 'createdAt' | 'updatedAt'>> {
    id?: string;
}
export async function upsertAthlete(
    athlete: UpsertAthlete,
    description = 'updateAthlete',
): Promise<DbAthlete | null> {
    return toast.promise(
        async () => {
            breadcrumb('upsertAthlete', { athlete, description });
            const { id, birthday, weight, gender, grade, activeSince } = athlete;

            const { data, error } = await supabase
                .from(ATHLETES)
                .upsert({
                    id,
                    birthday,
                    weight,
                    gender,
                    grade,
                    activeSince,
                })
                .select()
                .single();

            if (error) {
                log(`[upsertAthlete]: ${error.message}`, { error, athlete, description });
                throw new Error(error.message);
            } else {
                console.log(`(${description}) success `, data);
                return data;
            }
        },
        {
            pending: `${athlete?.id ? 'Creating' : 'Updating'} athlete...`,
            success: 'Athlete updated',
            error: 'Error updating athlete',
        },
    );
}

export async function updateUserAthlete(
    userAndAthlete: TeamAthlete | AddAthleteFormValues | Partial<AthleteTeamMember>,
    description = 'updateUserAthlete',
): Promise<DbAthlete | null | undefined> {
    return toast.promise(
        async () => {
            breadcrumb('updateUserAthlete', { athlete: userAndAthlete, description });

            invariant(userAndAthlete.id, 'Athlete must have an id to update');

            if (userAndAthlete.email) updateUserEmail(userAndAthlete.id, userAndAthlete.email);

            // update user first
            updateUserAndMembership({
                id: userAndAthlete.id,
                firstName: userAndAthlete.firstName,
                lastName: userAndAthlete.lastName,
            }); // throws on error, handled by toast

            const { birthday, weight, gender, grade, activeSince } = userAndAthlete;

            if (birthday || weight || gender || activeSince || grade) {
                return upsertAthlete(userAndAthlete as UpsertAthlete, description);
            } else {
                log(`[updateUserAthlete]: missing athlete fields`);
            }
        },
        {
            pending: 'Updating athlete...',
            success: 'Athlete updated',
            error: 'Error updating athlete',
        },
    );
}

/**
 * Returns child athletes for a user
 * @param userId Parent or Signed in user
 * @returns children & associated athletes
 */
export const getUserAthletes = async (userId: string | undefined): Promise<UserAthlete[]> => {
    if (!userId) return [];

    const { data, error } = await supabase
        .from('UserAthlete')
        .select(
            `
            id:athleteId,
            *
        `,
        )
        .eq('parentOrUserId', userId);

    if (error) {
        log(`[getUserAthletes]: ${error.message}`);
        toast.error('Error fetching user athletes');
        return [];
    }

    breadcrumb('getUserAthletes', { userId, data });

    return data as UserAthlete[];
};

/**
 * Gets athlete info, optionally logs error if not found
 * @param userId
 * @param expectData - if true, logs error if not found
 * @returns
 */
export const getAthlete = async (
    userId: string | undefined,
    expectData = true,
): Promise<DbAthlete | null> => {
    if (!userId) return null;

    const { data, error } = await supabase.from(ATHLETES).select(`*`).eq('id', userId).single();

    if (error) {
        if (expectData) {
            log(`[getAthlete]: ${error.message}`);
        }
        return null;
    }

    breadcrumb('getAthlete', { userId, data });

    return data as DbAthlete;
};

export const getTeamMembers = async (teamId: string | undefined): Promise<TeamAthlete[]> => {
    if (!teamId) return [];

    const { data, error } = await supabase.from(TEAM_MEMBERSHIP).select('*').eq('teamId', teamId);

    if (error) {
        log(`[getTeamMembers]: ${error.message}`);
        toast('Error fetching team members');
        return [];
    }

    breadcrumb('getTeamMembers', { teamId, data });

    return data.map(({ email, ...d }) => ({
        ...d,
        email: hidePlaceholderEmail(email),
    })) as TeamAthlete[];
};

export const getRelatedAthletes = async (): Promise<RelatedAthlete[]> => {
    const user = await getSignedInUser();
    invariant(user, 'User must be signed in to get related athletes');

    const { data, error } = await supabase
        .from('RelatedAthletes')
        .select('*')
        .eq('userId', user?.id)
        .eq('relationStatus', STATUS.ACTIVE);

    if (error) {
        log(`[getRelatedAthletes]: ${error.message}`);
        toast.error('Error fetching related athletes');
        return [];
    }

    breadcrumb('getRelatedAthletes', data);

    return data as RelatedAthlete[];
};
